import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarHeart, ClipboardList, CakeSlice, Copy, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const ease = [0.22, 1, 0.36, 1];

const OCCASIONS = ["Wedding", "Birthday", "Anniversary", "Baby Shower", "Corporate", "Just Because"];
const SERVINGS = ['6" cake (serves 10–15)', '8" cake (serves 20–30)', "Two-tier cake", "Bespoke multi-tier — priced on consultation", "Cupcakes / brownies / cookies", "Not sure yet"];
const FLAVOURS = [
  "Signature Velvet Rose",
  "Vanilla Bean & Berry",
  "Rich Dark Chocolate Ganache",
  "Salted Caramel & Pecan",
  "Lemon Elderflower",
  "Something else — let's talk",
];

const STEPS = [
  { icon: ClipboardList, title: "Tell us your vision", text: "Share your occasion, date and dream design below." },
  { icon: CalendarHeart, title: "Lall confirms the details", text: "You'll hear back personally within 24 hours with a quote." },
  { icon: CakeSlice, title: "Beautifully baked", text: "Your cake is handcrafted fresh for your celebration." },
];

const initial = { name: "", email: "", phone: "", occasion: OCCASIONS[1], event_date: "", servings: SERVINGS[0], flavour: FLAVOURS[0], dietary: "", notes: "" };

const parsePrice = (s) => {
  const m = /£\s*(\d+)/.exec(s || "");
  return m ? parseInt(m[1], 10) : 0;
};

const fmtDay = (d) => {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch {
    return d;
  }
};

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#8B5D6C] mb-2">{children}</label>
);

const OrderForm = () => {
  const [form, setForm] = useState(initial);
  const [pricing, setPricing] = useState(null);
  const [extras, setExtras] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    api.get("/pricing").then((res) => setPricing(res.data)).catch(() => {});
    api.get("/availability").then((res) => setBlocked(res.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isBlocked = Boolean(form.event_date) && blocked.some((b) => b.date === form.event_date);

  const extrasSection = pricing?.sections?.find((s) => s.id === "extras");

  const base = useMemo(() => {
    if (!pricing) return null;
    const token = form.servings.split(" ")[0].toLowerCase();
    for (const sec of pricing.sections) {
      for (const it of sec.items) {
        if (it.name.toLowerCase().includes(token)) return { name: it.name, price: parsePrice(it.price) };
      }
    }
    return null;
  }, [pricing, form.servings]);

  const extrasTotal = extras.reduce((sum, id) => {
    const it = extrasSection?.items.find((i) => i.id === id);
    return sum + (it ? parsePrice(it.price) : 0);
  }, 0);

  const estimate = base ? base.price + extrasTotal : extrasTotal > 0 ? extrasTotal : null;

  const toggleExtra = (id) =>
    setExtras((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (isBlocked) return;
    setSending(true);
    try {
      const extrasNames = extras
        .map((id) => extrasSection?.items.find((i) => i.id === id)?.name)
        .filter(Boolean);
      const res = await api.post("/orders", {
        ...form,
        extras: extrasNames,
        estimate: estimate ? `From £${estimate}` : "",
      });
      toast.success("Your order request is with Lall — she'll be in touch within 24 hours.");
      setSubmitted({ name: form.name, token: res.data.track_token });
      setForm(initial);
      setExtras([]);
    } catch (err) {
      toast.error(formatApiError(err, "Could not send your request. Please try again."));
    } finally {
      setSending(false);
    }
  };

  const trackingUrl = submitted ? `${window.location.origin}/track/${submitted.token}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(trackingUrl);
      toast.success("Tracking link copied — keep it somewhere safe.");
    } catch {
      toast.error("Copy didn't work — long-press the link instead.");
    }
  };

  return (
    <section id="order" className="relative py-24 md:py-32 bg-[#F3EEE6]/70" data-testid="order-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease }}
          className="lg:col-span-5"
        >
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#8B5D6C] mb-4">Bespoke Order Request</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A2834] leading-tight">
            Tell us about your occasion &amp; cake dreams
          </h2>
          <p className="mt-6 text-[#70505D] leading-relaxed">
            Every Velvet Bakes creation begins with a conversation. Share the details
            below and Lall will personally come back to you with ideas and a quote.
          </p>
          <div className="mt-12 space-y-8">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.12, ease }}
                className="flex gap-5"
              >
                <div className="shrink-0 w-12 h-12 rounded-full border border-[#C5A059]/60 flex items-center justify-center text-[#C5A059]">
                  <s.icon size={20} />
                </div>
                <div>
                  <p className="font-serif text-xl text-[#4A2834]">{s.title}</p>
                  <p className="text-sm text-[#70505D] mt-1">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease }}
          className="lg:col-span-7 bg-[#F9F6F0] border border-[#C5A059]/30 rounded-3xl p-8 md:p-12 shadow-xl shadow-[#8B5D6C]/10"
        >
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease }}
                className="text-center py-6"
                data-testid="order-success-panel"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-[#8B5D6C] border border-[#C5A059]/60 flex items-center justify-center text-[#F9F6F0]">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-[#4A2834] mt-6">
                  Thank you, {submitted.name.split(" ")[0]}
                </h3>
                <p className="mt-4 text-[#70505D] text-sm md:text-base leading-relaxed max-w-md mx-auto">
                  Your request is with Lall and she'll reply personally within 24 hours.
                  In the meantime, here's your private link to follow your order's progress:
                </p>
                <div className="mt-6 rounded-2xl border border-[#C5A059]/40 bg-[#F3EEE6]/80 p-5 max-w-md mx-auto">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#8B5D6C] mb-2">Your tracking link</p>
                  <p className="text-xs text-[#70505D] break-all mb-4" data-testid="order-tracking-link">{trackingUrl}</p>
                  <button
                    onClick={copyLink}
                    data-testid="order-copy-link-button"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#8B5D6C] text-[#F9F6F0] text-xs font-medium tracking-wide border border-[#C5A059]/60 hover:bg-[#4A2834] transition-colors"
                  >
                    <Copy size={14} /> Copy my link
                  </button>
                  <p className="mt-4 text-[11px] text-[#987885] leading-relaxed">
                    Save it somewhere safe — you'll also receive it by email once your order is accepted.
                  </p>
                </div>
                <button
                  onClick={() => setSubmitted(null)}
                  data-testid="order-another-button"
                  className="mt-8 text-sm text-[#8B5D6C] border-b border-[#C5A059] pb-1 hover:text-[#4A2834] transition-colors"
                >
                  Send another request
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={submit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease }}
                data-testid="order-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label>Your name</Label>
                    <input className="field" required value={form.name} onChange={set("name")} placeholder="e.g. Amara Shah" data-testid="order-name-input" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <input className="field" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="order-email-input" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <input className="field" required value={form.phone} onChange={set("phone")} placeholder="07…" data-testid="order-phone-input" />
                  </div>
                  <div>
                    <Label>Date needed</Label>
                    <input
                      className={`field ${isBlocked ? "!border-red-400" : ""}`}
                      type="date"
                      required
                      value={form.event_date}
                      onChange={set("event_date")}
                      data-testid="order-date-input"
                    />
                    {isBlocked && (
                      <p className="mt-2 text-xs text-red-500" data-testid="order-date-blocked-warning">
                        Lall is fully booked on this date — please choose another.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Occasion</Label>
                    <select className="field" value={form.occasion} onChange={set("occasion")} data-testid="order-occasion-select">
                      {OCCASIONS.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Size / servings</Label>
                    <select className="field" value={form.servings} onChange={set("servings")} data-testid="order-servings-select">
                      {SERVINGS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  {blocked.length > 0 && (
                    <div className="sm:col-span-2 -my-2">
                      <p className="text-xs text-[#987885] leading-relaxed" data-testid="order-blocked-dates">
                        <span className="uppercase tracking-[0.2em] font-semibold text-[#8B5D6C]">Fully booked:</span>{" "}
                        {blocked.slice(0, 8).map((b) => fmtDay(b.date)).join("  ·  ")}
                      </p>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Label>Flavour</Label>
                    <select className="field" value={form.flavour} onChange={set("flavour")} data-testid="order-flavour-select">
                      {FLAVOURS.map((f) => <option key={f}>{f}</option>)}
                    </select>
                  </div>
                  {extrasSection && (
                    <div className="sm:col-span-2">
                      <Label>Finishing touches (optional)</Label>
                      <div className="flex flex-wrap gap-2.5" data-testid="order-extras">
                        {extrasSection.items.map((it) => {
                          const on = extras.includes(it.id);
                          return (
                            <button
                              type="button"
                              key={it.id}
                              onClick={() => toggleExtra(it.id)}
                              data-testid={`order-extra-${it.id}`}
                              className={`px-4 py-2.5 rounded-full text-xs tracking-wide border transition-all duration-300 ${
                                on
                                  ? "bg-[#8B5D6C] text-[#F9F6F0] border-[#C5A059]/70"
                                  : "border-[#8B5D6C]/25 text-[#70505D] hover:border-[#C5A059]"
                              }`}
                            >
                              {it.name} · {it.price.replace("From ", "+")}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Label>Dietary requirements (optional)</Label>
                    <input className="field" value={form.dietary} onChange={set("dietary")} placeholder="Gluten-free, nut-free, dairy-free…" data-testid="order-dietary-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Design vision &amp; notes</Label>
                    <textarea
                      className="field min-h-[120px] resize-y"
                      value={form.notes}
                      onChange={set("notes")}
                      placeholder="Colours, themes, inspiration — anything that helps us picture your cake."
                      data-testid="order-notes-input"
                    />
                  </div>
                </div>

                {estimate !== null && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease }}
                    className="mt-8 rounded-2xl border border-[#C5A059]/40 bg-[#F3EEE6]/80 px-6 py-5 flex items-center justify-between gap-6"
                    data-testid="order-estimate"
                  >
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B5D6C]">Estimated starting price</p>
                      <p className="text-xs text-[#987885] mt-1.5 leading-relaxed">
                        {base ? `Based on ${base.name}${extrasTotal > 0 ? " plus your finishing touches" : ""}.` : "Finishing touches selected."}{" "}
                        Lall confirms your final quote personally.
                      </p>
                    </div>
                    <p className="font-serif text-4xl text-[#4A2834] whitespace-nowrap" data-testid="order-estimate-value">from £{estimate}</p>
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={sending || isBlocked}
                  data-testid="order-submit-button"
                  className="mt-8 w-full sm:w-auto px-10 py-4 rounded-full bg-[#8B5D6C] text-[#F9F6F0] text-sm font-medium tracking-wide border border-[#C5A059]/60 hover:bg-[#4A2834] disabled:opacity-60 transition-colors duration-300"
                >
                  {sending ? "Sending…" : isBlocked ? "Fully booked that day" : "Send my order request"}
                </button>
                <p className="mt-4 text-xs text-[#987885]">No payment needed now — Lall confirms every order personally.</p>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default OrderForm;
